import React, { useMemo } from "react";
import { Sprite } from "@pixi/react";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { PixiSettings } from "../../../../pixiSettings";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

// Company logo configuration - matches UI logo dimensions
const COMPANY_LOGO_CONFIG = {
  BASE_WIDTH: 175,
  BASE_HEIGHT: 34,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.05,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 0.95,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
      deviceMultiplier: 0.85,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.0,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 0.9,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.6,
    },
  },
};

const CompanyLogo = ({ toolbar }) => {
  const { getResponsiveConfig, device, orientation, dimensions, isMobileSmall } =
    useDimensions();
  const texture = getNewUITexture("company_logo");

  // Get responsive configuration from context - memoized to prevent recalculation
  const logoConfig = useMemo(
    () => getResponsiveConfig(COMPANY_LOGO_CONFIG, true),
    [getResponsiveConfig]
  );
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Get logo position based on device and orientation (similar to PixiButtonSound) - memoized
  const logoPosition = useMemo(() => {
    let x = toolbar.width / 2; // Default center
    let y = toolbar.centerY; // Default center Y

    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        x = buttonConfig.width * 1.2;
        y = buttonConfig.height * 0.45;
      } else {
        // Landscape
        x = toolbar.width - buttonConfig.width * 1.0;
        y = buttonConfig.height * 0.7;
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        x = buttonConfig.width * 2.2;
        y = buttonConfig.height * 1.4;
      } else {
        // Landscape
        // x = toolbar.width - buttonConfig.width * 0.4;
        // Its right EDGE on the toolbar's edge (FEATURES_CONFIG.LAYOUT.NO_TABLET):
        // the logo is about two buttons wide, so a centre 0.4 of a button in
        // left half of it past the screen once the toolbar filled the width.
        x = FEATURES_CONFIG.LAYOUT.NO_TABLET
          ? toolbar.width - logoConfig.width / 2
          : toolbar.width - buttonConfig.width * 0.4;
        y = buttonConfig.height * 0.65;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        x = buttonConfig.width * 2.5;
        y = buttonConfig.height * 0.8;
      } else {
        // Landscape
        x = buttonConfig.width * 2.0;
        y = dimensions.height - buttonConfig.height * 2.25;
      }
    } else {
      // Desktop - above balance display, centered over it
      const balanceX = toolbar.layout?.balance?.x ?? buttonConfig.width * 2.5;
      const balanceW = toolbar.layout?.balance?.width ?? 0;
      x = balanceX + balanceW / 2;
      y = dimensions.height - toolbar.height - logoConfig.height * 1.0;
    }

    return { x, y };
  }, [
    device,
    orientation,
    isMobileSmall,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    toolbar.layout,
    toolbar.centerY,
    buttonConfig.width,
    buttonConfig.height,
    logoConfig.width,
    logoConfig.height,
  ]);

  return (
    <Sprite
      name="secondary_company_logo"
      x={logoPosition.x}
      y={logoPosition.y}
      zIndex={3}
      texture={texture}
      anchor={0.5}
      width={logoConfig.width}
      height={logoConfig.height}
      roundPixels={true}
    />
  );
};

export default CompanyLogo;
